/**
 * Rethink Firestore-like Client — REST + WebSocket bridge.
 *
 * Transport/socket lifecycle lives in `./connection`, reference/snapshot
 * classes in `./references`, the live-query engine in `./subscribe`, and
 * protocol types in `./protocol`.
 */

import { hydrateTimestamps } from './hydrate';
import { BACKEND_URL, ConnectionManager } from './connection';
import type { DocumentData, Protocol } from './protocol';
import {
  CollectionReference,
  DocumentReference,
  Query,
  RethinkDocumentSnapshot,
  RethinkQueryDocumentSnapshot,
  type DocumentSnapshot,
  type QueryDocumentSnapshot,
  type QuerySnapshot,
} from './references';
import { watchQuery } from './subscribe';

/**
 * Rethink Firestore-like Client
 */
export class RethinkClient {
  private connection: ConnectionManager;
  private identity: { token: string } | null = null;
  private identityListeners: Array<() => void> = [];

  constructor() {
    this.connection = new ConnectionManager();
  }

  setIdentity(id: { token: string } | null) {
    this.identity = id;
    // Propagate to the socket layer so pending (previously anonymous)
    // subscriptions are re-emitted with the fresh token.
    this.connection.setIdentity(id);
    // Notify active snapshots so they can refetch their initial data, which
    // may have failed with 403 before the token was ready.
    if (id?.token) {
      this.identityListeners.forEach((listener) => listener());
    }
  }

  onIdentityChange(listener: () => void): () => void {
    this.identityListeners.push(listener);
    return () => {
      this.identityListeners = this.identityListeners.filter((l) => l !== listener);
    };
  }

  private get headers(): HeadersInit {
    const h: HeadersInit = { 'Content-Type': 'application/json' };
    if (this.identity?.token) {
      h['Authorization'] = `Bearer ${this.identity.token}`;
    }
    return h;
  }

  collection<T = DocumentData>(name: string) {
    return new CollectionReference<T>(this, name);
  }

  collectionGroup<T = DocumentData>(name: string) {
    return new Query<T>(new CollectionReference<T>(this, name), [], true);
  }

  doc<T = DocumentData>(collectionName: string, id: string) {
    return new DocumentReference<T>(this, collectionName, id);
  }

  async getDocs<T = DocumentData>(q: Query<T> | CollectionReference<T>): Promise<QuerySnapshot<T>> {
    const queryObj = q instanceof CollectionReference ? new Query<T>(q) : q;
    const options = queryObj.buildOptions();
    const baseUrl = queryObj.isGroup
      ? `/api/collectionGroup/${queryObj.colRef.name}`
      : `/api/collections/${queryObj.colRef.name}`;

    const url = new URL(baseUrl, BACKEND_URL);
    url.searchParams.append('options', JSON.stringify(options));

    const response = await fetch(url.toString(), {
      headers: this.headers,
    });
    if (!response.ok) {
      if (response.status === 403) throw new Error('Permission denied by security rules');
      throw new Error(`Failed to fetch collection ${queryObj.colRef.name}`);
    }
    const rawData = await response.json();
    const hydratedData = hydrateTimestamps(rawData);

    const docsList = hydratedData.map((item: DocumentData) => {
      const docRef = new DocumentReference<T>(
        queryObj.colRef.client,
        queryObj.colRef.name,
        item.id,
      );
      // Wire JSON materialized as the doc type (adapter boundary).
      return new RethinkQueryDocumentSnapshot<T>(item.id, item as unknown as T, docRef);
    });

    return {
      docs: docsList,
      docChanges: () => [],
      empty: docsList.length === 0, // --- FIXED: Provided natively ---
      forEach(callback: (doc: QueryDocumentSnapshot<T>) => void, thisArg?: unknown) {
        docsList.forEach(callback, thisArg);
      },
    };
  }

  async getDoc<T = DocumentData>(docRef: DocumentReference<T>): Promise<DocumentSnapshot<T>> {
    const url = new URL(`/api/collections/${docRef.collectionName}/${docRef.id}`, BACKEND_URL);
    const response = await fetch(url.toString(), {
      headers: this.headers,
    });
    if (response.status === 404) {
      return new RethinkDocumentSnapshot<T>(docRef.id, undefined, false, docRef);
    }
    if (!response.ok) {
      if (response.status === 403) throw new Error('Permission denied by security rules');
      throw new Error(`Failed to fetch document ${docRef.id}`);
    }
    const rawData = await response.json();
    const hydratedDoc = hydrateTimestamps(rawData);

    // DocumentReference carries no converter (it lives on the collection);
    // the old `(docRef as any).converter` always read undefined.
    return new RethinkDocumentSnapshot<T>(docRef.id, hydratedDoc, true, docRef, undefined);
  }

  onSnapshot<T = DocumentData>(
    q: Query<T> | CollectionReference<T>,
    onNext: (snapshot: QuerySnapshot<T>) => void,
    onError?: (error: Error) => void,
  ) {
    // Live-query engine lives in `./subscribe`; the host facade keeps the
    // socket connection private to this class.
    return watchQuery(
      {
        getDocs: <T2>(qq: Query<T2> | CollectionReference<T2>) => this.getDocs(qq),
        onIdentityChange: (listener) => this.onIdentityChange(listener),
        subscribe: (table, options, isGroup, callback) =>
          this.connection.subscribe(table, options, isGroup, callback),
        hasIdentity: () => Boolean(this.identity?.token),
      },
      q,
      onNext,
      onError,
    );
  }

  async setDoc<T = DocumentData>(
    docRef: DocumentReference<T>,
    data: T,
    options: { merge?: boolean } = {},
  ) {
    const url = new URL(`/api/collections/${docRef.collectionName}/${docRef.id}`, BACKEND_URL);
    if (options.merge) url.searchParams.append('merge', 'true');

    const response = await fetch(url.toString(), {
      method: 'PUT',
      headers: this.headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(`Failed to set document ${docRef.id}`);
    const rawData = await response.json();
    return hydrateTimestamps(rawData);
  }

  async addDoc<T = DocumentData>(colRef: CollectionReference<T>, data: T) {
    const response = await fetch(
      new URL(`/api/collections/${colRef.name}`, BACKEND_URL).toString(),
      {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify(data),
      },
    );
    if (!response.ok) throw new Error(`Failed to add document to ${colRef.name}`);
    const rawData = await response.json();
    return hydrateTimestamps(rawData);
  }

  async updateDoc<T = DocumentData>(docRef: DocumentReference<T>, data: Partial<T>) {
    const response = await fetch(
      new URL(`/api/collections/${docRef.collectionName}/${docRef.id}`, BACKEND_URL).toString(),
      {
        method: 'PATCH',
        headers: this.headers,
        body: JSON.stringify(data),
      },
    );
    if (!response.ok) throw new Error(`Failed to update document ${docRef.id}`);
    const rawData = await response.json();
    return hydrateTimestamps(rawData);
  }

  async deleteDoc<T = DocumentData>(docRef: DocumentReference<T>) {
    const response = await fetch(
      new URL(`/api/collections/${docRef.collectionName}/${docRef.id}`, BACKEND_URL).toString(),
      {
        method: 'DELETE',
        headers: this.headers,
      },
    );
    if (!response.ok) throw new Error(`Failed to delete document ${docRef.id}`);
    const rawData = await response.json();
    return hydrateTimestamps(rawData);
  }

  async indexCreate(collectionName: string, indexName: string, fields: string[]) {
    const response = await fetch(
      new URL(`/api/collections/${collectionName}/index`, BACKEND_URL).toString(),
      {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify({ name: indexName, fields }),
      },
    );
    if (!response.ok) throw new Error(`Failed to create index ${indexName} on ${collectionName}`);
    return response.json();
  }

  writeBatch() {
    const operations: Protocol.BatchOperation[] = [];
    return {
      set: <T = DocumentData>(
        docRef: DocumentReference<T>,
        data: T,
        options: { merge?: boolean } = {},
      ) => {
        operations.push({
          type: 'set',
          collection: docRef.collectionName,
          id: docRef.id,
          data,
          options,
        });
      },
      update: <T = DocumentData>(docRef: DocumentReference<T>, data: Partial<T>) => {
        operations.push({ type: 'update', collection: docRef.collectionName, id: docRef.id, data });
      },
      delete: <T = DocumentData>(docRef: DocumentReference<T>) => {
        operations.push({ type: 'delete', collection: docRef.collectionName, id: docRef.id });
      },
      commit: async () => {
        const response = await fetch(new URL('/api/batch', BACKEND_URL).toString(), {
          method: 'POST',
          headers: this.headers,
          body: JSON.stringify({ operations }),
        });
        if (!response.ok) throw new Error('Batch commit failed');
        const rawData = await response.json();
        return hydrateTimestamps(rawData);
      },
    };
  }

  async runTransaction(
    updateFunction: (transaction: unknown) => Promise<unknown>,
  ): Promise<unknown> {
    const operations: Protocol.BatchOperation[] = [];
    const transaction = {
      get: async <T = DocumentData>(docRef: DocumentReference<T>) => {
        const snap = await this.getDoc(docRef);
        operations.push({ type: 'get', collection: docRef.collectionName, id: docRef.id });
        return snap;
      },
      set: <T = DocumentData>(
        docRef: DocumentReference<T>,
        data: T,
        options: { merge?: boolean } = {},
      ) => {
        operations.push({
          type: 'set',
          collection: docRef.collectionName,
          id: docRef.id,
          data,
          options,
        });
      },
      update: <T = DocumentData>(docRef: DocumentReference<T>, data: Partial<T>) => {
        operations.push({ type: 'update', collection: docRef.collectionName, id: docRef.id, data });
      },
      delete: <T = DocumentData>(docRef: DocumentReference<T>) => {
        operations.push({ type: 'delete', collection: docRef.collectionName, id: docRef.id });
      },
    };

    const result = await updateFunction(transaction);

    const response = await fetch(new URL('/api/transaction', BACKEND_URL).toString(), {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify({ operations }),
    });

    if (!response.ok) throw new Error('Transaction failed');
    return result;
  }

  async getCountFromServer<T = DocumentData>(q: Query<T> | CollectionReference<T>) {
    return this.aggregate(q, [{ type: 'count' }]);
  }

  async getSumFromServer<T = DocumentData>(q: Query<T> | CollectionReference<T>, field: string) {
    return this.aggregate(q, [{ type: 'sum', field }]);
  }

  async getAverageFromServer<T = DocumentData>(
    q: Query<T> | CollectionReference<T>,
    field: string,
  ) {
    return this.aggregate(q, [{ type: 'avg', field }]);
  }

  private async aggregate<T = DocumentData>(
    q: Query<T> | CollectionReference<T>,
    aggregations: Array<Record<string, unknown>>,
  ) {
    const queryObj = q instanceof CollectionReference ? new Query<T>(q) : q;
    const options = queryObj.buildOptions();

    const response = await fetch(
      new URL(`/api/aggregate/${queryObj.colRef.name}`, BACKEND_URL).toString(),
      {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify({ options, aggregations }),
      },
    );

    if (!response.ok) {
      if (response.status === 403) throw new Error('Permission denied by security rules');
      throw new Error('Aggregation failed');
    }

    const result = await response.json();
    return {
      data: () => result,
      count: result.count !== undefined ? result.count : undefined,
    };
  }

  async listCollections(): Promise<string[]> {
    const response = await fetch(new URL('/api/collections', BACKEND_URL).toString(), {
      headers: this.headers,
    });
    if (!response.ok) throw new Error('Failed to fetch collections');
    return response.json();
  }

  async createCollection(name: string): Promise<{ success: boolean }> {
    const response = await fetch(new URL('/api/collections', BACKEND_URL).toString(), {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify({ name }),
    });
    if (!response.ok) throw new Error('Failed to create collection');
    return response.json();
  }

  async getSystemInfo(): Promise<{ status: string; type: string }> {
    const response = await fetch(new URL('/api/health', BACKEND_URL).toString());
    if (!response.ok) throw new Error('Failed to fetch system info');
    return response.json();
  }
}

export const rethink = new RethinkClient();
